import React, {createRef, useMemo} from 'react';

import {Button, IButtonProps, InputGroup, MenuItem} from '@blueprintjs/core';
import {ISuggestProps, Suggest} from '@blueprintjs/select';
import classNames from 'classnames';
import {useQuery} from 'react-query';

import {fetchAllOmnivoreEmployeesRequest} from '../../api/omnivore.api';
import {useAuth} from '../../contexts/auth.context';
import {OmnivoreEmployeeDto} from '@kontactless/admin-api/omnivore-employee/omnivore-employee.dto';

import './selectors.styles.scss';

interface OmnivoreEmployeeSelectorProps
  extends Omit<ISuggestProps<OmnivoreEmployeeDto>, 'items' | 'itemRenderer' | 'onItemSelect' | 'inputValueRenderer'> {
  storeId: number;
  fill?: boolean;
  selectedItemId?: string;
  onItemSelected: (item: OmnivoreEmployeeDto | undefined) => void;
  buttonProps?: IButtonProps;
}

const OmnivoreEmployeeSelectorComponent = Suggest.ofType<OmnivoreEmployeeDto>();

export const OmnivoreEmployeeSelector: React.FC<OmnivoreEmployeeSelectorProps> = ({
  storeId,
  selectedItemId,
  onItemSelected,
  buttonProps,
  ...props
}) => {
  const {user} = useAuth();
  const employeesQuery = useQuery(['omnivore-employees', storeId], () => fetchAllOmnivoreEmployeesRequest(user.token, storeId));
  const inputRef = createRef<any>();

  const selectedItem = useMemo(() => {
    return employeesQuery.data?.find(({employeeId}) => employeeId === selectedItemId);
  }, [employeesQuery.data, selectedItemId]);

  const getEmployeeLabel = (employee: OmnivoreEmployeeDto): string => {
    return `(${employee.posId ?? ''}) - ${employee.firstName ?? ''}`;
  };

  if (employeesQuery.isError) {
    return <InputGroup {...(props.inputProps ?? {})} intent="danger" value="Error fetching omnivore employees" readOnly />;
  }

  return (
    <OmnivoreEmployeeSelectorComponent
      {...props}
      inputValueRenderer={(employee) => (employeesQuery.isFetching ? '' : getEmployeeLabel(employee))}
      className={classNames(props.className, {'bp3-fill': props.fill})}
      popoverProps={{minimal: true}}
      items={employeesQuery.data ?? []}
      itemRenderer={(employee, {modifiers, handleClick}) => (
        <MenuItem
          active={modifiers.active}
          disabled={modifiers.disabled}
          key={employee.employeeId}
          onClick={handleClick}
          text={getEmployeeLabel(employee)}
        />
      )}
      onItemSelect={(value) => onItemSelected(value)}
      itemsEqual={(e1, e2) => e1.employeeId === e2.employeeId}
      selectedItem={selectedItem ?? null}
      itemPredicate={(query, employee) =>
        !!employee.employeeId.toLocaleLowerCase().includes(query.toLowerCase()) ||
        !!employee.firstName?.toLocaleLowerCase().includes(query.toLowerCase())
      }
      noResults={<MenuItem disabled={true} text="No results." />}
      ref={inputRef}
      inputProps={{
        ...(props.inputProps ?? {}),
        leftElement: employeesQuery.isFetching ? <Button loading minimal /> : undefined,
        placeholder: employeesQuery.isFetching ? 'Loading employees...' : 'Choose one...',
        rightElement: <Button icon="chevron-down" minimal onClick={() => inputRef.current?.inputElement?.focus()} />,
      }}
    />
  );
};
